import { useEffect, useState } from 'react';
import { PasswordMeter } from '../components/SignIn.tsx';
import { fmtTime, useCtx } from '../lib.ts';

interface Me {
  userId: string;
  displayName: string;
  roleLabel: string;
  operatorId: string | null;
  mfaEnrolledAt: string | null;
  passwordSet: boolean;
  passwordChangedAt: string | null;
  lastLoginAt: string | null;
}

/** Own account: security state and password change. */
export function Account() {
  const { backend } = useCtx();
  const [me, setMe] = useState<Me | null>(null);
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    const t = setTimeout(() => void backend.server?.get<Me>('/v1/admin/account').then((r) => r.ok && setMe(r.value)), 0);
    return () => clearTimeout(t);
  }, [backend]);
  const auth = backend.auth.mode === 'login' ? backend.auth : null;
  if (!backend.server || !auth) return <p className="empty">Accounts need the UrbanRats backend.</p>;
  if (!me) return <p className="empty">Loading…</p>;
  return (
    <div className="users-page">
      <div className="cc-head">
        <div>
          <h1>My account</h1>
          <p className="note">
            {me.displayName} · {me.userId} · {me.roleLabel}
            {me.operatorId ? ` · operator ${me.operatorId}` : ''}
          </p>
        </div>
      </div>
      <div className="users-grid">
        <section className="card-x">
          <header className="card-h">
            <h2>Security</h2>
          </header>
          <dl className="kv">
            <dt>Authenticator app</dt>
            <dd>{me.mfaEnrolledAt ? `enrolled ${fmtTime(me.mfaEnrolledAt)}` : 'not enrolled'}</dd>
            <dt>Password</dt>
            <dd>{!me.passwordSet ? 'none (DEV seeded account)' : me.passwordChangedAt ? `changed ${fmtTime(me.passwordChangedAt)}` : 'issued by an admin — choose your own'}</dd>
            <dt>Last sign-in</dt>
            <dd>{fmtTime(me.lastLoginAt)}</dd>
          </dl>
          <p className="note">Lost your authenticator app? Ask an admin to reset your account (new one-time password, new QR code).</p>
        </section>
        {me.passwordSet && (
          <section className="card-x" data-testid="account-password">
            <header className="card-h">
              <h2>Change password</h2>
            </header>
            <form
              className="form"
              onSubmit={(e) => {
                e.preventDefault();
                if (next !== again) return setMsg({ ok: false, text: 'The two new passwords differ' });
                void auth.changePassword(cur, next).then((r) => {
                  setMsg(r.ok ? { ok: true, text: 'Password changed. Other sessions of your account were signed out.' } : { ok: false, text: r.error });
                  if (r.ok) {
                    setCur('');
                    setNext('');
                    setAgain('');
                  }
                });
              }}
            >
              <label htmlFor="ac-cur">Current password</label>
              <input id="ac-cur" type="password" value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" />
              <label htmlFor="ac-new">New password</label>
              <input id="ac-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
              <PasswordMeter pw={next} />
              <label htmlFor="ac-again">New password again</label>
              <input id="ac-again" type="password" value={again} onChange={(e) => setAgain(e.target.value)} autoComplete="new-password" />
              <button type="submit" className="btn primary" disabled={!cur || next.length < 10}>
                Change password
              </button>
              {msg && (
                <p role="status" className={msg.ok ? 'ok-text' : 'bad-text'}>
                  {msg.text}
                </p>
              )}
            </form>
          </section>
        )}
      </div>
    </div>
  );
}
